import { Menu, MenuItemRadio, MenuList, MenuPopover, MenuTrigger, makeStyles, tokens } from "@fluentui/react-components";
import { ArrowSort16Regular, Board16Regular, Grid16Regular, MoreHorizontal16Regular, TextBulletListLtr16Regular } from "@fluentui/react-icons";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListSearch, ListToolbar, useListToolbarStyles } from "@/components/list-toolbar";
import { centerFill, contentShell } from "@/shell/content-frame";
import { usePageAction } from "@/shell/page-action";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { PlanCardGrid, usePlanCards } from "@/features/plans/PlanCardGrid";
import { PLAN_MOCK_CASES, setPlanMockCase, usePlanMockCase } from "@/features/plans/plan-mock";
import { PLAN_SCOPES, PLAN_SORTS, parsePlanView, type PlanScope, type PlanSort, type PlanView } from "@/features/plans/plan-gallery";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { useProjectStore } from "@/store/project-store";

const useStyles = makeStyles({
  page: {
    ...contentShell,
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  empty: centerFill,
  mockBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "24px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    flexShrink: 0,
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
});

export function PlansPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const allowCreate = canWorkspace(useWorkspaceRole(), "plan.create");
  const [params, setParams] = useSearchParams();
  const rawView = params.get("view");
  const view = parsePlanView(rawView);
  const [scope, setScope] = useState<PlanScope>("all");
  const [sort, setSort] = useState<PlanSort>("updated");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (rawView === "team" || rawView === "budget") {
      navigate(`/portfolio?view=${rawView}`, { replace: true });
    }
  }, [rawView, navigate]);

  const openPlan = () => useProjectStore.getState().openDialog("create-plan");
  usePageAction(allowCreate ? { label: "플랜 추가", icon: Board16Regular, onClick: openPlan, inHeader: true } : null);
  const { hasPlans, cards, scale } = usePlanCards(scope, sort, query);

  const selectView = (next: PlanView) => {
    if (next === view) return;
    const updated = new URLSearchParams(params);
    if (next === "grid") updated.set("view", "grid");
    else updated.delete("view");
    setParams(updated, { replace: true });
  };

  return (
    <div className={styles.page}>
      <ListToolbar
        title="플랜"
        description={scale}
        titleAside={<PlanMockMenu />}
        headingAction={
          allowCreate ? (
            <FuiButton appearance="primary" icon={<Board16Regular />} onClick={openPlan}>
              플랜 추가
            </FuiButton>
          ) : undefined
        }
        search={hasPlans ? <ListSearch value={query} onChange={setQuery} /> : undefined}
        filters={
          hasPlans ? (
            <>
              <SegmentedControl
                ariaLabel="플랜 상태"
                value={scope}
                onChange={setScope}
                options={PLAN_SCOPES}
              />
              <SortMenu value={sort} onChange={setSort} />
            </>
          ) : undefined
        }
        view={
          hasPlans ? (
            <SegmentedControl
              ariaLabel="플랜 보기"
              value={view}
              onChange={selectView}
              options={[
                { value: "list", label: "목록", icon: <TextBulletListLtr16Regular /> },
                { value: "grid", label: "카드", icon: <Grid16Regular /> },
              ]}
            />
          ) : undefined
        }
      />
      {hasPlans ? (
        <PlanCardGrid view={view} cards={cards} empty={query.trim() ? "search" : "scope"} />
      ) : (
        <div className={styles.empty}>
          <GhostEmptyState title="아직 참여한 플랜이 없습니다" description="초대를 받거나 새 플랜을 만들어 보세요." />
        </div>
      )}
    </div>
  );
}

function PlanMockMenu() {
  const styles = useStyles();
  const scenario = usePlanMockCase();
  return (
    <Menu
      checkedValues={{ case: [scenario] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "empty" || next === "long" || next === "current") setPlanMockCase(next);
      }}
      positioning="below-start"
    >
      <MenuTrigger disableButtonEnhancement>
        <button type="button" className={styles.mockBtn} aria-label="목업 사례" title="목업 사례">
          <MoreHorizontal16Regular />
        </button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          {PLAN_MOCK_CASES.map((item) => (
            <MenuItemRadio key={item.id} name="case" value={item.id}>
              {item.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

function SortMenu({ value, onChange }: { value: PlanSort; onChange: (value: PlanSort) => void }) {
  const toolbar = useListToolbarStyles();
  const current = PLAN_SORTS.find((item) => item.value === value)?.label ?? "편집";
  return (
    <Menu
      checkedValues={{ sort: [value] }}
      onCheckedValueChange={(_, data) => {
        const next = data.checkedItems[0];
        if (next === "updated" || next === "name" || next === "finish") onChange(next);
      }}
    >
      <MenuTrigger disableButtonEnhancement>
        <FuiButton className={toolbar.nowrap} appearance="subtle" icon={<ArrowSort16Regular />} aria-label={`정렬: ${current}`}>
          {current}
        </FuiButton>
      </MenuTrigger>
      <MenuPopover>
        <MenuList aria-label="정렬">
          {PLAN_SORTS.map((item) => (
            <MenuItemRadio key={item.value} name="sort" value={item.value}>
              {item.label}
            </MenuItemRadio>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}
